import React, { useEffect, useRef, useState, useCallback } from "react";
import "./MediaPlayer.css";

interface AudioPlayerProps {
  audioUrl: string;
  chapter?:string;
  clip?:number
  skipSeconds?: number;
  autoPlay?: boolean;
}

const fmt = (s: number) => {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

const AudioPlayer = ({ audioUrl, chapter,clip, skipSeconds = 10, autoPlay = false }: AudioPlayerProps) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const dragging = useRef(false);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    setPlaying(false);
    setCurrent(0);
    setDuration(0);
    a.load();
    if (autoPlay) {
      const onCanPlay = () => {
        a.play();
        a.removeEventListener("canplay", onCanPlay);
      };
      a.addEventListener("canplay", onCanPlay);
      return () => a.removeEventListener("canplay", onCanPlay);
    }
  }, [audioUrl, autoPlay]);

  const seek = useCallback((clientX: number) => {
    const bar = barRef.current;
    const a = audioRef.current;
    if (!bar || !a || !a.duration) return;
    const { left, width } = bar.getBoundingClientRect();
    const ratio = Math.min(Math.max((clientX - left) / width, 0), 1);
    a.currentTime = ratio * a.duration;
    setCurrent(a.currentTime);
  }, []);

  const onMouseDown = (e: React.MouseEvent) => {
    dragging.current = true;
    seek(e.clientX);
  };

  useEffect(() => {
    const onMove = (e: MouseEvent) => { if (dragging.current) seek(e.clientX); };
    const onUp = () => { dragging.current = false; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [seek]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    playing ? a.pause() : a.play();
  };

  const skip = (sec: number) => {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = Math.min(Math.max(a.currentTime + sec, 0), a.duration || 0);
  };

  const progress = duration ? (current / duration) * 100 : 0;

  return (
    <div className="ap">
      <audio
        ref={audioRef}
        src={audioUrl}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onTimeUpdate={() => setCurrent(audioRef.current?.currentTime ?? 0)}
        onLoadedMetadata={() => setDuration(audioRef.current?.duration ?? 0)}
      />

    
      <div className="ap__controls">
        <button className="ap__skip" onClick={() => skip(-skipSeconds)} title={`-${skipSeconds}s`}>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
            <path d="M12 5V2L7 7l5 5V9c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/>
          </svg>
          <span>{skipSeconds}</span>
        </button>

        <button className="ap__play" onClick={toggle}>
          {playing
            ? <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
            : <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
          }
        </button>

        <button className="ap__skip" onClick={() => skip(skipSeconds)} title={`+${skipSeconds}s`}>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
            <path d="M12 5V2l5 5-5 5V9c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6h2c0 4.42-3.58 8-8 8s-8-3.58-8-8 3.58-8 8-8z"/>
          </svg>
          <span>{skipSeconds}</span>
        </button>
      </div>

      <div className="ap__progress-row">
        <span className="ap__time">{fmt(current)}</span>
        <div className="ap__bar-wrap" ref={barRef} onMouseDown={onMouseDown}>
          <div className="ap__bar">
            <div className="ap__bar-fill" style={{ width: `${progress}%` }}>
              <div className="ap__thumb" />
            </div>
          </div>
        </div>
        <span className="ap__time">{fmt(duration)}</span>
      </div>
    </div>
  );
};

export default AudioPlayer;
